import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Button, Table, message } from 'antd';
import { ArrowDownOutlined } from '@ant-design/icons';

import { database_columns, database_source } from './table_config';
import asyncFetch from '../../utils/asyncFetch';
import { asObject, asString } from '../../utils/dataGuards';
import dna from '../../images/dna.png';
import topoloy from '../../images/topoloy.png';
import flask from '../../images/flask.png';

const DOWNLOAD_FILES = [
    {
        fileType: 'gdc_variant',
        filename: 'gdc_variant.gz',
        title: 'All GDC Variants',
        icon: dna,
        iconAlt: 'dna',
        fallbackDate: 'July 30th, 2024',
        fallbackSize: '613M',
        description: 'This file contains all annotated variants in the GDC. Due to the internal data usage of the Chinese Deafness Genetics Consortium (CDGC), the download will be available upon the publication of the CDGC manuscript.'
    },
    {
        fileType: 'gdc_gene',
        filename: 'gene_data.gz',
        title: 'All GDC Genes',
        icon: topoloy,
        iconAlt: 'topoloy',
        fallbackDate: 'July 30th, 2024',
        fallbackSize: '180KB',
        description: 'This file contains all genes in the GDC.'
    },
    {
        fileType: 'dvpred_variant',
        filename: 'all_dvpred_variant.gz',
        title: 'All DVPred Variants',
        icon: flask,
        iconAlt: 'flask',
        fallbackDate: 'July 30th, 2024',
        fallbackSize: '28.3M',
        description: 'This file contains the site information of DvPred prediction results.'
    },
    {
        fileType: 'hearing_loss_genes',
        filename: 'candidate_hearing_loss_genes.7z',
        title: 'Candidate Hearing Loss Genes',
        icon: flask,
        iconAlt: 'flask',
        fallbackDate: 'Dec 16th, 2024',
        fallbackSize: '163k',
        description: 'This file contains information about candidate HL genes predicted by machine learning using a comprehensive expression and functional annotation dataset.'
    }
];

const getDownloadBaseUrl = () => (
    process.env.NODE_ENV === 'development' ? 'http://127.0.0.1:5000' : '/api'
);

const formatBytes = (value) => {
    const bytes = Number(value);
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return '';
    }
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const size = bytes / (1024 ** index);
    return `${size >= 10 || index === 0 ? size.toFixed(0) : size.toFixed(1)}${units[index]}`;
};

const formatDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

const Download = () => {
    const [fileMeta, setFileMeta] = useState({});

    useEffect(() => {
        DOWNLOAD_FILES.forEach(({ fileType }) => {
            asyncFetch('HEAD', '/download', {filetype: fileType},
                (response) => {
                    if (!response || response.status !== 200) {
                        return;
                    }
                    const size = response.headers.get('X-File-Size') || response.headers.get('Content-Length');
                    const modified = response.headers.get('X-File-Modified') || response.headers.get('Last-Modified');
                    setFileMeta((prev) => ({
                        ...prev,
                        [fileType]: {
                            size: formatBytes(size),
                            date: formatDate(modified)
                        }
                    }));
                },
            {}, 'cors', () => {}, true);
        });
    }, []);

    const download = async (fileType) => {
        const currentFile = DOWNLOAD_FILES.find((file) => file.fileType === fileType);
        const downloadFilename = currentFile?.filename || "download_file";
        const url = getDownloadBaseUrl();
        try {
            const link = document.createElement("a");
            link.href = `${url}/download?filetype=${fileType}`;
            link.setAttribute("download", downloadFilename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
    
        } catch (error) {
            message.error("Download error");
        }
    }

    return (
        <Row>
            <Col span={24} className="page-top-seize-seat" />
            <Col span={24} className="download-center download-contain">
                <span className="dvpred-title download-title">DOWNLOAD</span>
                <Row gutter={{xs: 6, sm: 12, md: 18, lg: 24}} style={{marginTop: '2vh'}}>
                    {DOWNLOAD_FILES.map((file) => {
                        const meta = asObject(fileMeta[file.fileType]);
                        const size = asString(meta.size) || file.fallbackSize;
                        const date = asString(meta.date) || file.fallbackDate;
                        return (
                            <Col xl={6} lg={12} md={24} sm={24} xs={24} key={file.fileType}>
                                <Card
                                    title={<span className='card-title'>{file.title}</span>}
                                    bordered={false}
                                    className="download-item"
                                    style={{minHeight: '320px'}}
                                >
                                    <div className="circle" />
                                    <img src={file.icon} alt={file.iconAlt} className="download-item-icon" />
                                    <span className="download-item-date">{date}</span>
                                    <p className="download-item-description">
                                        {file.description}
                                    </p>
                                    <Row className="download-item-bottom">
                                        <Col span={4}>{size}</Col>
                                        <Col span={20} style={{textAlign: 'right'}}>
                                            <Button icon={<ArrowDownOutlined />} className="download-btn" onClick={() => download(file.fileType)}>Download</Button>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            </Col>
            <Col span={24} className="download-contain">
                <span className="download-contain ant-card-head-title">PUBLIC DATA</span>
                <Table
                    bordered={false}
                    columns={database_columns}
                    dataSource={database_source}
                    scroll={{ x: 1200 }}
                />
            </Col>
        </Row>
    )
}

export default Download
